import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import type {
  AccountAttentionResponse,
  ComponentFlowResponse,
  CrossFlagsResponse,
  CustomerResponse,
  CustomersResponse,
  DatasetRequest,
  DatasetResponse,
  DeviceResponse,
  DistrictsResponse,
  DupSerialsResponse,
  ExploreResponse,
  FinanceResponse,
  FinanceWithMoMResponse,
  GeoHexResponse,
  GeoMetricsResponse,
  GeoStatesResponse,
  HealthResponse,
  LifecycleResponse,
  MemoryFactResponse,
  OverviewResponse,
  PipelineHealthResponse,
  RecipientScopedBundleResponse,
  RmaFlowWithDispositionResponse,
  Scope,
  SearchResponse,
  SupplyResponse,
  TelemetryResponse,
} from "@busright/data-contract";

import { apiClient } from "@/lib/api-client";
import { scopeKey } from "@/lib/scope";

// Per-endpoint TanStack Query hooks. Keys are namespaced by surface so the cache stays legible and
// each surface refetches independently.
export const queryKeys = {
  overview: ["overview"] as const,
  health: ["health"] as const,
  telemetry: ["telemetry"] as const,
  supply: ["supply"] as const,
  finance: ["finance"] as const,
  crossFlags: ["cross-flags"] as const,
  districts: ["districts"] as const,
  geoMetrics: ["geo", "metrics"] as const,
  geoHex: (metric: string) => ["geo", "hex", metric] as const,
  geoStates: ["geo", "states"] as const,
  customers: ["customers"] as const,
  customer: (id: string) => ["customer", id] as const,
  lifecycle: ["lifecycle"] as const,
  device: (serial: string) => ["device", serial] as const,
  pipelineHealth: ["pipeline-health"] as const,
  explore: (table: string) => ["explore", table] as const,
  search: (q: string) => ["search", q] as const,
  // Iteration D — drill engine + new scoped payloads.
  dataset: (req: DatasetRequest) =>
    ["dataset", req.metricKey, scopeKey(req.scope), req.filters ?? {}, req.cursor ?? null] as const,
  recipientBundle: (id: string, level: string) => ["recipient-bundle", id, level] as const,
  rmaFlow: ["rma-flow"] as const,
  memory: ["memory"] as const,
  financeMoM: ["finance-mom"] as const,
  // WS-B3 reporting-view endpoints.
  componentFlow: ["component-flow"] as const,
  accountsAttention: ["accounts-attention"] as const,
  dupSerials: ["dup-serials"] as const,
};

export const useOverview = (): UseQueryResult<OverviewResponse> =>
  useQuery({ queryKey: queryKeys.overview, queryFn: apiClient.getOverview });

export const useHealth = (): UseQueryResult<HealthResponse> =>
  useQuery({ queryKey: queryKeys.health, queryFn: apiClient.getHealth });

export const useTelemetry = (): UseQueryResult<TelemetryResponse> =>
  useQuery({ queryKey: queryKeys.telemetry, queryFn: apiClient.getTelemetry });

export const useSupply = (): UseQueryResult<SupplyResponse> =>
  useQuery({ queryKey: queryKeys.supply, queryFn: apiClient.getSupply });

export const useFinance = (): UseQueryResult<FinanceResponse> =>
  useQuery({ queryKey: queryKeys.finance, queryFn: apiClient.getFinance });

// AR × hardware-risk double-flags for the Finance surface. Independent of useFinance so each panel
// refetches on its own.
export const useCrossFlags = (): UseQueryResult<CrossFlagsResponse> =>
  useQuery({ queryKey: queryKeys.crossFlags, queryFn: apiClient.getCrossFlags });

export const useDistricts = (): UseQueryResult<DistrictsResponse> =>
  useQuery({ queryKey: queryKeys.districts, queryFn: apiClient.getDistricts });

export const useGeoMetrics = (): UseQueryResult<GeoMetricsResponse> =>
  useQuery({ queryKey: queryKeys.geoMetrics, queryFn: apiClient.getGeoMetrics });

// Hex cells for one metric. Pass `enabled: false` until a metric is chosen to avoid a wasted fetch.
export const useGeoHex = (metric: string, enabled = true): UseQueryResult<GeoHexResponse> =>
  useQuery({
    queryKey: queryKeys.geoHex(metric),
    queryFn: () => apiClient.getGeoHex(metric),
    enabled: enabled && metric.length > 0,
  });

export const useGeoStates = (): UseQueryResult<GeoStatesResponse> =>
  useQuery({ queryKey: queryKeys.geoStates, queryFn: apiClient.getGeoStates });

export const useCustomers = (): UseQueryResult<CustomersResponse> =>
  useQuery({ queryKey: queryKeys.customers, queryFn: apiClient.getCustomers });

export const useCustomer = (id: string): UseQueryResult<CustomerResponse> =>
  useQuery({
    queryKey: queryKeys.customer(id),
    queryFn: () => apiClient.getCustomer(id),
    enabled: id.length > 0,
  });

export const useLifecycle = (): UseQueryResult<LifecycleResponse> =>
  useQuery({ queryKey: queryKeys.lifecycle, queryFn: apiClient.getLifecycle });

export const useDevice = (serial: string): UseQueryResult<DeviceResponse> =>
  useQuery({
    queryKey: queryKeys.device(serial),
    queryFn: () => apiClient.getDevice(serial),
    enabled: serial.length > 0,
  });

export const usePipelineHealth = (): UseQueryResult<PipelineHealthResponse> =>
  useQuery({ queryKey: queryKeys.pipelineHealth, queryFn: apiClient.getPipelineHealth });

export const useExplore = (table: string): UseQueryResult<ExploreResponse> =>
  useQuery({
    queryKey: queryKeys.explore(table),
    queryFn: () => apiClient.getExplore(table),
    enabled: table.length > 0,
  });

// ── Iteration D — drill engine + new scoped payloads ────────────────────────────────────────────

// The generic drill hook: rows behind one metric at one scope. `enabled` lets a drawer hold the
// fetch until it's actually opened. The endpoint may not be live yet — on failure the consumer
// renders an error/empty state (the drawer never fabricates rows). `retry: false` so a missing
// endpoint surfaces immediately rather than spinning.
export const useDataset = (req: DatasetRequest, enabled = true): UseQueryResult<DatasetResponse> =>
  useQuery({
    queryKey: queryKeys.dataset(req),
    queryFn: () => apiClient.getDataset(req),
    enabled,
    retry: false,
  });

// The reusable District/Recipient scoped bundle. Disabled until an id is present.
export const useRecipientBundle = (scope: Scope): UseQueryResult<RecipientScopedBundleResponse> =>
  useQuery({
    queryKey: queryKeys.recipientBundle(scope.id ?? "", scope.level),
    queryFn: () => apiClient.getRecipientBundle(scope.id ?? "", scope),
    enabled: Boolean(scope.id),
    retry: false,
  });

export const useRmaFlow = (): UseQueryResult<RmaFlowWithDispositionResponse> =>
  useQuery({ queryKey: queryKeys.rmaFlow, queryFn: apiClient.getRmaFlow, retry: false });

export const useMemory = (): UseQueryResult<MemoryFactResponse> =>
  useQuery({ queryKey: queryKeys.memory, queryFn: apiClient.getMemory, retry: false });

// Finance widened with the MoM block. Separate key from useFinance so the existing lean finance
// query is untouched; this one tolerates the endpoint not yet serving `mom`.
export const useFinanceMoM = (): UseQueryResult<FinanceWithMoMResponse> =>
  useQuery({ queryKey: queryKeys.financeMoM, queryFn: apiClient.getFinanceMoM, retry: false });

// ── WS-B3 reporting-view endpoints (R3/R4, R11, R12) ────────────────────────────────────────────

// Component flow by month × product group (tablet/cradle/harness/sim) — supply-chain surface.
// Standalone + live-only per the contract, so `retry:false` surfaces a not-live endpoint honestly.
export const useComponentFlow = (): UseQueryResult<ComponentFlowResponse> =>
  useQuery({
    queryKey: queryKeys.componentFlow,
    queryFn: apiClient.getComponentFlow,
    retry: false,
  });

// Accounts requiring attention — churn-risk / dormant / pilot worklist for the Accounts surface.
export const useAccountsAttention = (): UseQueryResult<AccountAttentionResponse> =>
  useQuery({
    queryKey: queryKeys.accountsAttention,
    queryFn: apiClient.getAccountsAttention,
    retry: false,
  });

// Esper duplicate/blank serial rows — the data-quality surface. 100% real, no inference.
export const useDupSerials = (): UseQueryResult<DupSerialsResponse> =>
  useQuery({ queryKey: queryKeys.dupSerials, queryFn: apiClient.getDupSerials, retry: false });

// Debounce a fast-changing string so a per-keystrokes input doesn't fire a query on every key.
const SEARCH_DEBOUNCE_MS = 220;

export const useDebounced = <T>(value: T, delayMs = SEARCH_DEBOUNCE_MS): T => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
};

// Cross-surface lookup for the global search bar. The caller passes the RAW input; this hook
// debounces it internally and only fetches once the (trimmed) query is at least 2 chars, so a
// single keystroke never hits the API. Disabled while empty/short so the dropdown stays quiet.
const SEARCH_MIN_CHARS = 2;

export const useSearch = (rawQuery: string): UseQueryResult<SearchResponse> => {
  const q = useDebounced(rawQuery).trim();
  return useQuery({
    queryKey: queryKeys.search(q),
    queryFn: () => apiClient.search(q),
    enabled: q.length >= SEARCH_MIN_CHARS,
  });
};
